import { useMemo } from "react";
import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  CATALOG_MUSCLE_SLUGS,
  muscleKey,
  type CatalogMuscleSlug,
} from "@heraion/shared";

import type { UiTheme } from "../../theme";
import { makeCoveragePanelStyles } from "./coveragePanelStyles";

export type CoveragePanelProps = {
  theme: UiTheme;
  /** hardSetsByMuscleFromDraft(programDraft, catalog) — the full-cycle
   *  weekly average, sparse (an absent slug reads as 0). */
  hardSetsByMuscle: Partial<Record<CatalogMuscleSlug, number>>;
};

// PE-22 K2.3 — the coverage readout (mockup §03: "Pettorali 18 · Polpacci
// 0"). READ-ONLY, computed on the draft-in-construction — its whole reason
// to exist is that a muscle at zero is visible WHILE the program is being
// written, not six weeks into following it. So every catalog muscle
// renders, including the zeros: a sparse list that hid them would defeat
// the point.
export function CoveragePanel({ theme, hardSetsByMuscle }: CoveragePanelProps) {
  const { t } = useTranslation();
  const styles = useMemo(() => makeCoveragePanelStyles(theme), [theme]);

  const rows = useMemo(
    () =>
      CATALOG_MUSCLE_SLUGS.map((slug) => ({
        slug,
        label: t(muscleKey(slug)),
        count: Math.round(hardSetsByMuscle[slug] ?? 0),
      })),
    [t, hardSetsByMuscle],
  );

  const a11yLabel = rows.map((r) => `${r.label} ${r.count}`).join(", ");

  return (
    <View style={styles.wrap} accessible accessibilityLabel={a11yLabel}>
      <Text style={styles.heading} accessibilityRole="header">
        {t("composer.coverageTitle", { defaultValue: "Coverage — hard sets/week" })}
      </Text>
      <Text style={styles.hint}>
        {t("composer.coverageHint", {
          defaultValue: "Full-cycle average. Zero shows here before you've trained it.",
        })}
      </Text>
      <Text style={styles.body}>
        {rows.map((r, i) => (
          <Text key={r.slug} style={r.count === 0 ? styles.zero : undefined}>
            {`${i > 0 ? " · " : ""}${r.label} ${r.count}`}
          </Text>
        ))}
      </Text>
    </View>
  );
}
